import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { MdPhotoCamera } from 'react-icons/md';
import LoginFormRedesigned from './LoginFormRedesigned';

import AdminPanel from './AdminPanel';

// Add GSAP CDN
if (typeof window !== 'undefined' && !window.gsap) {
  const script = document.createElement('script');
  script.src = 'https://cdnjs.cloudflare.com/ajax/libs/gsap/3.12.2/gsap.min.js';
  script.async = true;
  document.head.appendChild(script);
}

const AssetValuator = () => {
  // Force cache clear
  const cacheVersion = Date.now() + Math.random();
  
  // Clear browser cache on component mount
  useEffect(() => {
    if ('caches' in window) {
      caches.keys().then(names => {
        names.forEach(name => caches.delete(name));
      });
    }
    // Force reload without cache
    if (window.location.search.indexOf('nocache') === -1) {
      window.location.href = window.location.href + '?nocache=' + Date.now();
    }
  }, []);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [userInput, setUserInput] = useState('');
  const [uploadedImage, setUploadedImage] = useState(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const [showFeedbackOptions, setShowFeedbackOptions] = useState(false);
  const [questionHistory, setQuestionHistory] = useState([]);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [userAuth, setUserAuth] = useState(null);
  const [showAdminPanel, setShowAdminPanel] = useState(false);

  const [pendingImage, setPendingImage] = useState(null);
  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    // Load Google Identity Services
    const gsiScript = document.createElement('script');
    gsiScript.src = 'https://accounts.google.com/gsi/client';
    gsiScript.async = true;
    document.head.appendChild(gsiScript);
    
    // Load Google API Platform Library
    const gapiScript = document.createElement('script');
    gapiScript.src = 'https://apis.google.com/js/api.js';
    gapiScript.async = true;
    document.head.appendChild(gapiScript);
    
    // Check for stored auth
    const storedAuth = localStorage.getItem('userAuth');
    if (storedAuth) {
      try {
        setUserAuth(JSON.parse(storedAuth));
      } catch (e) {
        localStorage.removeItem('userAuth');
      }
    }
    
    // Show loading screen for 3 seconds
    const timer = setTimeout(() => {
      setIsInitializing(false);
    }, 3000);
    return () => clearTimeout(timer);
  }, []);

  // GSAP animations for welcome content
  useEffect(() => {
    if (!isInitializing && messages.length === 0 && window.gsap) {
      const tl = window.gsap.timeline({ delay: 0.5 });
      
      tl.to('.welcome-content h3', {
        opacity: 1,
        y: 0,
        duration: 0.8,
        ease: 'power3.out'
      })
      .to('.welcome-content p', {
        opacity: 1,
        y: 0,
        duration: 0.6,
        ease: 'power2.out'
      }, '-=0.4')
      .to('.capability', {
        opacity: 1,
        x: 0,
        duration: 0.5,
        stagger: 0.2,
        ease: 'back.out(1.7)'
      }, '-=0.3')
      .to('.ai-avatar', {
        scale: 1.1,
        duration: 0.3,
        yoyo: true,
        repeat: 1,
        ease: 'power2.inOut'
      }, '-=0.5');
    }
  }, [isInitializing, messages.length]);

  const API_BASE = 'https://qeiasnifog.execute-api.ap-southeast-2.amazonaws.com/prod/myworth-ai-valuator';

  const addMessage = (message, isUser = false) => {
    setMessages(prev => [...prev, { ...message, isUser, timestamp: new Date() }]);
    setTimeout(() => scrollToBottom(), 100);
  };

  const getQuestionOptions = () => {
    // Use options from AI response if available
    if (currentQuestion?.payload?.options) {
      return currentQuestion.payload.options;
    }
    
    // Fallback options
    return ['Yes', 'No', 'Other'];
  };

  const getCategoryIcon = (category) => {
    const icons = {
      'electronics': '📱',
      'vehicle': '🚗',
      'furniture': '🪑',
      'jewelry': '💍',
      'appliance': '🏠',
      'instrument': '🎸',
      'sports': '⚽',
      'collectible': '🎨',
      'tools': '🔧',
      'other': '📦'
    };
    return icons[category?.toLowerCase()] || '📦';
  };

  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = error => reject(error);
    });
  };

  const handleAuthSuccess = (authData) => {
    setUserAuth(authData);
    localStorage.setItem('userAuth', JSON.stringify(authData));
    
    // Process pending image if exists
    if (pendingImage) {
      setTimeout(() => {
        processImageUpload(pendingImage.file, authData);
        setPendingImage(null);
      }, 500);
    }
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!userAuth) {
      setPendingImage({ file, name: file.name });
      setShowAuthModal(true);
      return;
    }

    await processImageUpload(file, userAuth);
  };

  const processImageUpload = async (file, authData) => {
    setLoading(true);
    
    // Store image for display
    const imageUrl = URL.createObjectURL(file);
    setUploadedImage({ url: imageUrl, name: file.name });
    
    addMessage({ 
      content: `Uploaded: ${file.name}`, 
      type: 'image',
      imageUrl: imageUrl
    }, true);

    try {
      const base64 = await convertToBase64(file);
      
      console.log('Sending request with token:', authData.token?.substring(0, 20));
      const response = await axios.post(`${API_BASE}/detect-product`, {
        action: 'detect',
        auth_token: authData.token,
        image_b64: base64
      });

      const { session_id, llm_instruction, detection } = response.data;
      setSessionId(session_id);
      
      // Show detection result with image
      if (detection) {
        setCurrentQuestion(llm_instruction);
        addMessage({
          content: detection.message || `I can see a ${detection.detected_object}. Is this correct?`,
          type: 'detection',
          detection,
          imageUrl: imageUrl
        });
        setTimeout(() => scrollToBottom(), 100);
      } else if (llm_instruction?.payload?.message) {
        setCurrentQuestion(llm_instruction);
        addMessage({
          content: llm_instruction.payload.message,
          type: llm_instruction.action === 'reject_upload' ? 'rejection' : 'question'
        });
      }

    } catch (error) {
      console.log('API Error:', error.response?.status, error.response?.data);
      if (error.response?.status === 401) {
        // Clear invalid token and force re-authentication
        setUserAuth(null);
        localStorage.removeItem('userAuth');
        setShowAuthModal(true);
        addMessage({
          content: `Session expired. Please login again. (Token: ${authData.token?.substring(0, 20)}...)`,
          type: 'error'
        });
      } else {
        addMessage({
          content: `Error: ${error.response?.data?.error || error.message}`,
          type: 'error'
        });
      }
    } finally {
      setLoading(false);
      setTimeout(() => scrollToBottom(), 200);
    }
  };

  const handleSmartSubmit = async () => {
    if (!userInput?.trim()) return;
    await handleTextSubmit();
  };

  const handleTextSubmit = async () => {
    if (!userInput?.trim()) return;

    if (!userAuth) {
      setShowAuthModal(true);
      return;
    }

    setLoading(true);
    addMessage({ content: userInput, type: 'text_input' }, true);

    try {
      const response = await axios.post(`${API_BASE}/text-valuation`, {
        text_description: userInput.trim(),
        auth_token: userAuth.token
      });

      const { session_id, llm_instruction, detection } = response.data;
      setSessionId(session_id);
      
      if (detection) {
        setCurrentQuestion(llm_instruction);
        addMessage({
          content: detection.message || `You want to value: ${detection.detected_object}. Is this correct?`,
          type: 'detection',
          detection
        });
      } else if (llm_instruction?.payload?.message) {
        setCurrentQuestion(llm_instruction);
        addMessage({
          content: llm_instruction.payload.message,
          type: llm_instruction.action === 'reject_upload' ? 'rejection' : 'question'
        });
      }

    } catch (error) {
      if (error.response?.status === 401) {
        // Clear invalid token and force re-authentication
        setUserAuth(null);
        localStorage.removeItem('userAuth');
        setShowAuthModal(true);
        addMessage({
          content: 'Your session has expired. Please login again.',
          type: 'error'
        });
      } else {
        addMessage({
          content: `Error: ${error.response?.data?.error || error.message}`,
          type: 'error'
        });
      }
    } finally {
      setLoading(false);
      setUserInput('');
    }
  };

  const handleFeedback = async (type, session_id) => {
    if (type === 'positive') {
      // Store positive feedback
      try {
        await axios.post(`${API_BASE}/store-feedback`, {
          session_id: session_id,
          feedback_type: 'positive',
          timestamp: new Date().toISOString()
        });
        
        addMessage({
          content: 'Thank you for your feedback! 😊',
          type: 'feedback_success'
        });
        
        // Show new valuation button
        setTimeout(() => {
          setShowFeedbackOptions(true);
        }, 1000);
        
      } catch (error) {
        console.error('Feedback error:', error);
      }
    } else {
      // Handle negative feedback
      addMessage({
        content: 'I\'m sorry the valuation wasn\'t accurate. Would you like to:',
        type: 'feedback_options'
      });
      setShowFeedbackOptions(true);
    }
  };

  const handleReviseAnswers = () => {
    // Show question history for revision
    addMessage({
      content: 'Which question would you like to revise?',
      type: 'revision_options',
      questionHistory: questionHistory
    });
  };

  const handleNewValuation = () => {
    // Reset all state completely
    setSessionId(null);
    setMessages([]);
    setCurrentQuestion(null);
    setUserInput('');
    setUploadedImage(null);
    setShowFeedbackOptions(false);
    setQuestionHistory([]);
    setLoading(false);
    
    // Clear file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleAnswerSubmit = async (answer) => {
    if (!sessionId || !answer.trim()) return;

    setLoading(true);
    
    // Mark current question as answered
    setMessages(prev => prev.map(msg => 
      msg.type === 'question' && !msg.answered ? 
        { ...msg, answered: true, selectedAnswer: answer } : msg
    ));
    
    addMessage({ content: answer, type: 'answer' }, true);
    
    // Store question and answer in history
    if (currentQuestion) {
      const field = currentQuestion.payload?.field;
      setQuestionHistory(prev => {
        // Check if this field already exists (revision case)
        const existingIndex = prev.findIndex(item => item.field === field);
        if (existingIndex !== -1) {
          // Update existing answer
          const updated = [...prev];
          updated[existingIndex] = {
            ...updated[existingIndex],
            answer: answer
          };
          return updated;
        } else {
          // Add new question
          return [...prev, {
            question: currentQuestion.payload?.question,
            field: field,
            answer: answer,
            options: currentQuestion.payload?.options
          }];
        }
      });
    }

    try {
      const response = await axios.post(`${API_BASE}/answer-question`, {
        action: 'answer',
        session_id: sessionId,
        answer: answer.trim(),
        field: currentQuestion?.payload?.field || 'confirmation'
      });

      const { llm_instruction } = response.data;
      
      if (llm_instruction?.action === 'valuation_result') {
        const valuation = llm_instruction.payload;
        addMessage({
          content: 'Valuation Complete!',
          type: 'valuation',
          valuation
        });
        setCurrentQuestion(null);
        setTimeout(() => scrollToBottom(), 100);
      } else if (llm_instruction?.payload?.message) {
        setCurrentQuestion(llm_instruction);
        addMessage({
          content: llm_instruction.payload.message,
          type: 'question'
        });
      } else if (llm_instruction?.payload?.question) {
        setCurrentQuestion(llm_instruction);
        addMessage({
          content: llm_instruction.payload.question,
          type: 'question',
          options: llm_instruction.payload.options
        });
        setTimeout(() => scrollToBottom(), 200);
      }

    } catch (error) {
      if (error.response?.status === 401) {
        // Clear invalid token and force re-authentication
        setUserAuth(null);
        localStorage.removeItem('userAuth');
        setShowAuthModal(true);
        addMessage({
          content: 'Your session has expired. Please login again.',
          type: 'error'
        });
      } else {
        addMessage({
          content: `Error: ${error.response?.data?.error || error.message}`,
          type: 'error'
        });
      }
    } finally {
      setLoading(false);
      setUserInput('');
    }
  };

  const reviseSpecificAnswer = (field, options) => {
    // Add revision question to messages
    addMessage({
      content: `Please provide a new answer for ${field}:`,
      type: 'question',
      options: options
    });
    
    // Set current question for options display
    setCurrentQuestion({
      payload: {
        question: `Please provide a new answer for ${field}:`,
        field: field,
        options: options
      }
    });
    
    // Update question history to replace the old answer
    setQuestionHistory(prev => prev.map(item => 
      item.field === field ? { ...item, answer: 'Revising...' } : item
    ));
  };

  const renderMessage = (message, index) => {
    const { content, type, isUser, valuation, detection, imageUrl } = message;

    if (type === 'valuation' && valuation) {
      return (
        <div key={index} className="message assistant valuation">
          <div className="valuation-card">
            <h3>🎯 Asset Valuation Result</h3>
            <div className="price-range">
              <span className="currency">{valuation.currency}</span>
              <span className="range">
                ${valuation.value_low?.toLocaleString()} - ${valuation.value_high?.toLocaleString()}
              </span>
            </div>
            <div className="best-estimate">
              Best Estimate: <strong>{valuation.currency} ${valuation.best_estimate?.toLocaleString()}</strong>
            </div>
            <div className="confidence">
              Confidence: {Math.round((valuation.confidence || 0) * 100)}%
            </div>
            {valuation.reasoning && (
              <div className="reasoning">
                <strong>Reasoning:</strong> {valuation.reasoning}
              </div>
            )}
            <div className="feedback-section">
              <h4>How was this valuation?</h4>
              <div className="feedback-buttons">
                <button 
                  className="feedback-btn happy"
                  onClick={() => handleFeedback('positive', sessionId)}
                  disabled={loading}
                >
                  😊 Accurate
                </button>
                <button 
                  className="feedback-btn sad"
                  onClick={() => handleFeedback('negative', sessionId)}
                  disabled={loading}
                >
                  😞 Not Accurate
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (type === 'detection' && detection) {
      return (
        <div key={index} className="message assistant detection">
          <div className="detection-card">
            <div className="detection-content">
              {imageUrl && (
                <div className="detection-image">
                  <img src={imageUrl} alt="Uploaded asset" onError={(e) => console.log('Image load error:', e)} />
                </div>
              )}
              <div className="detection-info">
                <div className="detection-header">
                  <span className="detection-icon">{getCategoryIcon(detection.category)}</span>
                  <div className="detection-text">
                    <h4 className="detected-name">{detection.detected_object}</h4>
                    <div className="detection-meta">
                      <span className="category">{detection.category}</span>
                      <span className="confidence">{Math.round((detection.confidence || 0) * 100)}% match</span>
                    </div>
                  </div>
                </div>
                <p className="detection-message">{detection.message}</p>
              </div>
            </div>
            <div className="detection-actions">
              <button 
                className="action-btn confirm"
                onClick={() => handleAnswerSubmit('yes')}
                disabled={loading}
              >
                ✓ Correct
              </button>
              <button 
                className="action-btn reject"
                onClick={() => handleAnswerSubmit('no')}
                disabled={loading}
              >
                ✗ Wrong
              </button>
            </div>
          </div>
        </div>
      );
    }

    if (type === 'rejection') {
      return (
        <div key={index} className="message assistant rejection">
          <div className="rejection-card">
            <div className="rejection-content">
              <div className="rejection-icon">🚫</div>
              <div className="rejection-text">{content}</div>
              <div className="restart-actions">
                <button 
                  onClick={handleNewValuation}
                  className="restart-btn"
                  disabled={loading}
                >
                  <span className="btn-icon">🔄</span>
                  <span>Try Again</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (type === 'feedback_options') {
      return (
        <div key={index} className="message assistant feedback-options">
          <div className="message-content">{content}</div>
          <div className="feedback-actions">
            <button 
              onClick={handleReviseAnswers}
              className="revise-btn"
              disabled={loading}
            >
              🔄 Revise My Answers
            </button>
            <button 
              onClick={handleNewValuation}
              className="new-valuation-btn"
              disabled={loading}
            >
              🆕 Value Different Item
            </button>
          </div>
        </div>
      );
    }

    if (type === 'revision_options' && message.questionHistory) {
      return (
        <div key={index} className="message assistant revision-options">
          <div className="message-content">{content}</div>
          <div className="revision-list">
            {message.questionHistory.map((item, idx) => (
              <div key={idx} className="revision-item">
                <div className="revision-question">{item.question}</div>
                <div className="revision-answer">Your answer: {item.answer}</div>
                <button 
                  onClick={() => reviseSpecificAnswer(item.field, item.options)}
                  className="revise-specific-btn"
                >
                  Change Answer
                </button>
              </div>
            ))}
          </div>
        </div>
      );
    }

    return (
      <div key={index} className={`message ${isUser ? 'user' : 'assistant'} ${type}`}>
        {imageUrl && type === 'image' && (
          <div className="message-image">
            <img src={imageUrl} alt="Uploaded" />
          </div>
        )}
        <div className="message-content">{content}</div>
        {type === 'question' && message.options && !message.answered && (
          <div className="question-options">
            {message.options.map(option => (
              <button 
                key={option}
                onClick={() => handleAnswerSubmit(option)}
                disabled={loading}
                className="question-option-btn"
              >
                {option}
              </button>
            ))}
          </div>
        )}
        {type === 'question' && message.answered && (
          <div className="answered-indicator">
            ✓ Answered: {message.selectedAnswer}
          </div>
        )}
        {showFeedbackOptions && type === 'feedback_success' && (
          <div className="final-actions">
            <button 
              onClick={handleNewValuation}
              className="new-valuation-final-btn"
            >
              🆕 Value Another Item
            </button>
          </div>
        )}
      </div>
    );
  };

  if (isInitializing) {
    return (
      <>
        <div className="loading-screen">
          <div className="ai-loader">
            <div className="ai-brain">
              <div className="brain-core"></div>
              <div className="neural-ring ring-1"></div>
              <div className="neural-ring ring-2"></div>
              <div className="neural-ring ring-3"></div>
            </div>
            <div className="loading-text">
              <h2>AI Asset Valuator</h2>
              <p>Initializing Advanced AI Engine...</p>
              <div className="loading-dots">
                <span></span><span></span><span></span>
              </div>
            </div>
          </div>
        </div>
        <style>{`
          .loading-screen {
            position: fixed;
            top: 0;
            left: 0;
            width: 100vw;
            height: 100vh;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 9999;
          }
          .ai-loader {
            text-align: center;
            color: white;
          }
          .ai-brain {
            position: relative;
            width: 120px;
            height: 120px;
            margin: 0 auto 30px;
          }
          .brain-core {
            position: absolute;
            top: 50%;
            left: 50%;
            width: 40px;
            height: 40px;
            background: rgba(255,255,255,0.9);
            border-radius: 50%;
            transform: translate(-50%, -50%);
            animation: pulse 2s infinite;
          }
          .neural-ring {
            position: absolute;
            top: 50%;
            left: 50%;
            border: 2px solid rgba(255,255,255,0.3);
            border-radius: 50%;
            transform: translate(-50%, -50%);
            animation: rotate 3s linear infinite;
          }
          .ring-1 {
            width: 60px;
            height: 60px;
            animation-duration: 2s;
          }
          .ring-2 {
            width: 80px;
            height: 80px;
            animation-duration: 3s;
            animation-direction: reverse;
          }
          .ring-3 {
            width: 100px;
            height: 100px;
            animation-duration: 4s;
          }
          .loading-text h2 {
            font-size: 28px;
            margin-bottom: 10px;
            font-weight: 700;
          }
          .loading-text p {
            font-size: 16px;
            opacity: 0.9;
            margin-bottom: 20px;
          }
          .loading-dots {
            display: flex;
            justify-content: center;
            gap: 8px;
          }
          .loading-dots span {
            width: 8px;
            height: 8px;
            background: white;
            border-radius: 50%;
            animation: bounce 1.4s infinite ease-in-out;
          }
          .loading-dots span:nth-child(2) {
            animation-delay: 0.2s;
          }
          .loading-dots span:nth-child(3) {
            animation-delay: 0.4s;
          }
          @keyframes rotate {
            from { transform: translate(-50%, -50%) rotate(0deg); }
            to { transform: translate(-50%, -50%) rotate(360deg); }
          }
          @keyframes pulse {
            0%, 100% { transform: translate(-50%, -50%) scale(1); opacity: 1; }
            50% { transform: translate(-50%, -50%) scale(1.2); opacity: 0.7; }
          }
          @keyframes bounce {
            0%, 80%, 100% { transform: scale(0.8); opacity: 0.5; }
            40% { transform: scale(1.2); opacity: 1; }
          }
        `}</style>
      </>
    );
  }

  return (
    <div className="asset-valuator">
      <div className="premium-header">
        <div className="header-left">
          <div className="logo-section">
            <img 
              src={`${process.env.PUBLIC_URL}/logo.png`} 
              alt="AI Valuator" 
              className="logo-image"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
            <div className="logo-fallback" style={{display: 'none'}}>🤖</div>
            <div className="brand-text">
              <h1>AI Valuator</h1>
              <div className="tagline">Professional Asset Analysis</div>
            </div>
          </div>
        </div>
        
        <div className="header-right">
          {userAuth ? (
            <div className="user-info">
              <span className="user-email">{userAuth.email}</span>
              {userAuth.email === 'admin@myworth.com' && (
                <button 
                  onClick={() => setShowAdminPanel(true)}
                  className="admin-btn"
                >
                  ⚙️
                </button>
              )}
              <button 
                onClick={() => {
                  setUserAuth(null);
                  localStorage.removeItem('userAuth');
                  setMessages([]);
                  setSessionId(null);
                }}
                className="logout-btn"
                title="Logout"
              >
                ↗️
              </button>
            </div>
          ) : (
            <button 
              onClick={() => setShowAuthModal(true)}
              className="login-btn"
              title="Login"
            >
              🔑
            </button>
          )}
        </div>

      </div>

      <div className="premium-chat-container">
        <div className="messages">
          {messages.length === 0 && (
            <div className="ai-welcome">
              <div className="ai-avatar">
                <div className="avatar-glow"></div>
                <span className="avatar-icon">🤖</span>
              </div>
              <div className="welcome-content">
                <h3>Hello! I'm your AI Asset Valuator</h3>
                <p>I'll analyze your item and provide a professional market valuation based on current Australian prices.</p>
                <div className="capabilities">
                  <div className="capability">
                    <span className="cap-icon">🔍</span>
                    <span>Advanced Image Recognition</span>
                  </div>
                  <div className="capability">
                    <span className="cap-icon">📊</span>
                    <span>Real-time Market Analysis</span>
                  </div>
                  <div className="capability">
                    <span className="cap-icon">🎯</span>
                    <span>Professional Accuracy</span>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {messages.map(renderMessage)}
          
          {loading && (
            <div className="ai-thinking">
              <div className="thinking-avatar">
                <div className="thinking-brain">
                  <div className="neural-network">
                    <div className="node"></div>
                    <div className="node"></div>
                    <div className="node"></div>
                    <div className="node"></div>
                    <div className="connection"></div>
                    <div className="connection"></div>
                    <div className="connection"></div>
                  </div>
                </div>
              </div>
              <div className="thinking-text">
                <span className="typewriter">AI is analyzing</span>
                <div className="thinking-dots">
                  <span></span><span></span><span></span>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        <div className="input-area">
          {!userAuth ? (
            <div className="login-required">
              <p>🔒 Please login to start valuing your assets</p>
              <button 
                onClick={() => setShowAuthModal(true)}
                className="login-required-btn"
              >
                🔑 Login to Continue
              </button>
            </div>
          ) : !sessionId ? (
            <div className="unified-input">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleImageUpload}
                accept="image/*"
                style={{ display: 'none' }}
              />
              <div className="input-field">
                <input
                  type="text"
                  value={userInput || ''}
                  onChange={(e) => setUserInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSmartSubmit()}
                  placeholder="Describe your item or upload image (e.g., iPhone 15 Pro 256GB)"
                  disabled={loading}
                  className="smart-input-field"
                />
                <button 
                  onClick={() => fileInputRef.current?.click()}
                  className="attach-btn"
                  disabled={loading}
                  title="Upload image"
                >
                  <MdPhotoCamera />
                </button>
                <button 
                  onClick={handleSmartSubmit}
                  disabled={loading || !userInput?.trim()}
                  className="smart-submit-btn"
                >
                  Value Item
                </button>
              </div>
            </div>
          ) : currentQuestion ? (
            <div className="text-input">
              <input
                type="text"
                value={userInput || ''}
                onChange={(e) => setUserInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAnswerSubmit(userInput)}
                placeholder="Type your answer..."
                disabled={loading}
                className="chat-answer-input"
              />
              <button 
                onClick={() => handleAnswerSubmit(userInput)}
                disabled={loading || !userInput.trim()}
                className="chat-send-btn"
              >
                Send
              </button>
            </div>
          ) : null}
        </div>
      </div>

      <LoginFormRedesigned 
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onSuccess={handleAuthSuccess}
      />
      
      {showAdminPanel && userAuth && userAuth.email === 'admin@myworth.com' && (
        <AdminPanel 
          adminAuth={userAuth}
          onClose={() => setShowAdminPanel(false)}
        />
      )}

      <style>{`
        .loading-screen {
          position: fixed;
          top: 0;
          left: 0;
          width: 100vw;
          height: 100vh;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
        }

        .ai-loader {
          text-align: center;
          color: white;
        }

        .ai-brain {
          position: relative;
          width: 120px;
          height: 120px;
          margin: 0 auto 30px;
        }

        .brain-core {
          position: absolute;
          top: 50%;
          left: 50%;
          width: 40px;
          height: 40px;
          background: rgba(255,255,255,0.9);
          border-radius: 50%;
          transform: translate(-50%, -50%);
          animation: pulse 2s infinite;
        }

        .neural-ring {
          position: absolute;
          top: 50%;
          left: 50%;
          border: 2px solid rgba(255,255,255,0.3);
          border-radius: 50%;
          transform: translate(-50%, -50%);
          animation: rotate 3s linear infinite;
        }

        .ring-1 {
          width: 60px;
          height: 60px;
          animation-duration: 2s;
        }

        .ring-2 {
          width: 80px;
          height: 80px;
          animation-duration: 3s;
          animation-direction: reverse;
        }

        .ring-3 {
          width: 100px;
          height: 100px;
          animation-duration: 4s;
        }

        .loading-text h2 {
          font-size: 28px;
          margin-bottom: 10px;
          font-weight: 700;
        }

        .loading-text p {
          font-size: 16px;
          opacity: 0.9;
          margin-bottom: 20px;
        }

        .loading-dots {
          display: flex;
          justify-content: center;
          gap: 8px;
        }

        .loading-dots span {
          width: 8px;
          height: 8px;
          background: white;
          border-radius: 50%;
          animation: bounce 1.4s infinite ease-in-out;
        }

        .loading-dots span:nth-child(2) {
          animation-delay: 0.2s;
        }

        .loading-dots span:nth-child(3) {
          animation-delay: 0.4s;
        }

        @keyframes rotate {
          from { transform: translate(-50%, -50%) rotate(0deg); }
          to { transform: translate(-50%, -50%) rotate(360deg); }
        }

        @keyframes pulse {
          0%, 100% { transform: translate(-50%, -50%) scale(1); opacity: 1; }
          50% { transform: translate(-50%, -50%) scale(1.2); opacity: 0.7; }
        }

        @keyframes bounce {
          0%, 80%, 100% { transform: scale(0.8); opacity: 0.5; }
          40% { transform: scale(1.2); opacity: 1; }
        }

        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        body, html {
          overflow: hidden;
        }

        .asset-valuator {
          width: 100vw;
          height: 100vh;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          background: linear-gradient(135deg, #0f0f23 0%, #1a1a2e 50%, #16213e 100%);
          display: flex;
          flex-direction: column;
          overflow: hidden;
          position: fixed;
          top: 0;
          left: 0;
        }

        .premium-header {
          background: rgba(255, 255, 255, 0.05);
          backdrop-filter: blur(20px);
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
          padding: 15px 20px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-shrink: 0;
        }
        
        @media (max-width: 768px) {
          .premium-header {
            padding: 12px 15px;
            flex-direction: column;
            gap: 10px;
          }
          
          .header-right {
            display: none;
          }
          
          .brand-text h1 {
            font-size: 20px;
          }
          
          .brand-text .tagline {
            font-size: 11px;
          }
        }

        .header-left {
          display: flex;
          align-items: center;
          gap: 15px;
        }

        .logo-section {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .logo-image {
          width: 120px;
          height: 60px;
          border-radius: 12px;
          object-fit: contain;
          background: white;
          padding: 4px;
        }
        
        .logo-fallback {
          width: 120px;
          height: 60px;
          background: white;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 30px;
        }

        .brand-text {
          color: white;
        }

        .brand-text h1 {
          font-size: 24px;
          font-weight: 700;
          margin-bottom: 2px;
        }

        .brand-text .tagline {
          font-size: 12px;
          color: rgba(255, 255, 255, 0.7);
          font-weight: 400;
        }

        .header-right {
          display: flex;
          align-items: center;
          gap: 20px;
        }

        .status-indicators {
          display: flex;
          align-items: center;
          gap: 20px;
        }

        .status-item {
          display: flex;
          align-items: center;
          gap: 8px;
          color: rgba(255, 255, 255, 0.8);
          font-size: 14px;
          font-weight: 500;
        }

        .status-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: #28a745;
          animation: pulse-dot 2s infinite;
        }

        .accuracy {
          background: rgba(40, 167, 69, 0.2);
          color: #28a745;
          padding: 4px 12px;
          border-radius: 12px;
          font-size: 12px;
          font-weight: 600;
        }

        @keyframes pulse-dot {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }

        .premium-chat-container {
          flex: 1;
          display: flex;
          flex-direction: column;
          background: rgba(255, 255, 255, 0.02);
          margin: 0;
          border-radius: 0;
          border: 1px solid rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(20px);
          overflow: hidden;
        }
        
        @media (max-width: 768px) {
          .premium-chat-container {
            margin: 10px;
            border-radius: 16px;
          }
        }

        .ai-welcome {
          display: flex;
          align-items: center;
          gap: 25px;
          padding: 50px;
          background: linear-gradient(135deg, rgba(168, 85, 247, 0.4) 0%, rgba(139, 69, 19, 0.4) 100%);
          border-radius: 24px;
          border: 1px solid rgba(255, 255, 255, 0.3);
          margin-bottom: 30px;
          backdrop-filter: blur(15px);
          box-shadow: 0 8px 32px rgba(168, 85, 247, 0.2);
        }
        
        @media (max-width: 768px) {
          .ai-welcome {
            flex-direction: column;
            text-align: center;
            padding: 30px 20px;
            gap: 20px;
          }
          
          .welcome-content h3 {
            font-size: 22px;
          }
          
          .welcome-content p {
            font-size: 14px;
          }
          
          .capabilities {
            align-items: center;
          }
        }

        .ai-avatar {
          position: relative;
          width: 80px;
          height: 80px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          cursor: pointer;
          transition: transform 0.3s ease;
        }
        
        .ai-avatar:hover {
          transform: scale(1.05) rotate(5deg);
        }

        .avatar-glow {
          position: absolute;
          width: 100%;
          height: 100%;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border-radius: 50%;
          animation: glow 2s infinite alternate;
        }

        .avatar-icon {
          position: relative;
          font-size: 40px;
          z-index: 1;
        }

        .welcome-content h3 {
          font-size: 28px;
          color: #ffffff;
          margin-bottom: 12px;
          font-weight: 700;
          text-shadow: 0 0 20px rgba(255, 255, 255, 0.8), 0 2px 4px rgba(0, 0, 0, 0.3);
          filter: brightness(1.3);
          opacity: 0;
          transform: translateY(30px);
        }

        .welcome-content p {
          color: #ffffff;
          margin-bottom: 25px;
          line-height: 1.6;
          font-size: 16px;
          text-shadow: 0 0 15px rgba(255, 255, 255, 0.6), 0 1px 2px rgba(0, 0, 0, 0.2);
          filter: brightness(1.2);
          opacity: 0;
          transform: translateY(20px);
        }

        .capabilities {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .capability {
          display: flex;
          align-items: center;
          gap: 12px;
          color: #ffffff;
          font-size: 14px;
          font-weight: 500;
          padding: 8px 0;
          text-shadow: 0 0 10px rgba(255, 255, 255, 0.4);
          filter: brightness(1.1);
          opacity: 0;
          transform: translateX(-20px);
        }

        .cap-icon {
          font-size: 16px;
        }

        .ai-thinking {
          display: flex;
          align-items: center;
          gap: 15px;
          padding: 24px;
          background: linear-gradient(135deg, rgba(34, 193, 195, 0.3) 0%, rgba(253, 187, 45, 0.3) 100%);
          margin: 16px 20px;
          border-radius: 20px;
          border: 1px solid rgba(34, 193, 195, 0.4);
          backdrop-filter: blur(10px);
          box-shadow: 0 6px 24px rgba(34, 193, 195, 0.2);
          animation: breathe 2s ease-in-out infinite;
        }

        @keyframes breathe {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.02); }
        }

        .neural-network {
          position: relative;
          width: 30px;
          height: 30px;
        }

        .node {
          position: absolute;
          width: 6px;
          height: 6px;
          background: #667eea;
          border-radius: 50%;
          animation: pulse-node 1.5s ease-in-out infinite;
        }

        .node:nth-child(1) { top: 0; left: 0; animation-delay: 0s; }
        .node:nth-child(2) { top: 0; right: 0; animation-delay: 0.3s; }
        .node:nth-child(3) { bottom: 0; left: 0; animation-delay: 0.6s; }
        .node:nth-child(4) { bottom: 0; right: 0; animation-delay: 0.9s; }

        .connection {
          position: absolute;
          background: #667eea;
          height: 1px;
          animation: flow 2s ease-in-out infinite;
        }

        .connection:nth-child(5) {
          top: 3px;
          left: 6px;
          width: 18px;
          transform: rotate(0deg);
        }

        .connection:nth-child(6) {
          top: 15px;
          left: 3px;
          width: 20px;
          transform: rotate(45deg);
        }

        .connection:nth-child(7) {
          top: 15px;
          left: 6px;
          width: 18px;
          transform: rotate(-45deg);
        }

        @keyframes pulse-node {
          0%, 100% { opacity: 0.3; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.5); }
        }

        @keyframes flow {
          0%, 100% { opacity: 0.2; }
          50% { opacity: 0.8; }
        }

        .typewriter {
          animation: typewriter 2s steps(13) infinite;
        }

        @keyframes typewriter {
          0% { width: 0; }
          50% { width: 13ch; }
          100% { width: 0; }
        }

        .thinking-avatar {
          width: 50px;
          height: 50px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border-radius: 50%;
          position: relative;
        }

        .thinking-brain {
          position: relative;
          width: 24px;
          height: 24px;
        }

        .synapse {
          position: absolute;
          width: 4px;
          height: 4px;
          background: white;
          border-radius: 50%;
          animation: synapse 1.5s infinite;
        }

        .synapse:nth-child(1) {
          top: 2px;
          left: 10px;
          animation-delay: 0s;
        }

        .synapse:nth-child(2) {
          top: 10px;
          left: 2px;
          animation-delay: 0.5s;
        }

        .synapse:nth-child(3) {
          top: 18px;
          left: 18px;
          animation-delay: 1s;
        }

        .thinking-text {
          color: #34495e;
          font-weight: 600;
        }

        .thinking-dots {
          display: inline-flex;
          gap: 4px;
          margin-left: 8px;
        }

        .thinking-dots span {
          width: 6px;
          height: 6px;
          background: #667eea;
          border-radius: 50%;
          animation: bounce 1.4s infinite ease-in-out;
        }

        .thinking-dots span:nth-child(2) {
          animation-delay: 0.2s;
        }

        .thinking-dots span:nth-child(3) {
          animation-delay: 0.4s;
        }

        @keyframes glow {
          from { box-shadow: 0 0 20px rgba(102, 126, 234, 0.3); }
          to { box-shadow: 0 0 30px rgba(102, 126, 234, 0.6); }
        }

        @keyframes synapse {
          0%, 100% { opacity: 0.3; transform: scale(0.8); }
          50% { opacity: 1; transform: scale(1.2); }
        }

        .feedback-section {
          margin-top: 25px;
          padding-top: 20px;
          border-top: 1px solid rgba(255,255,255,0.2);
          text-align: center;
        }

        .feedback-section h4 {
          margin-bottom: 15px;
          font-size: 16px;
        }

        .feedback-buttons {
          display: flex;
          gap: 15px;
          justify-content: center;
        }

        .feedback-btn {
          padding: 12px 20px;
          border: 2px solid rgba(255,255,255,0.3);
          background: rgba(255,255,255,0.1);
          color: white;
          border-radius: 25px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 600;
          transition: all 0.3s;
        }

        .feedback-btn:hover {
          background: rgba(255,255,255,0.2);
          transform: scale(1.05);
        }

        .feedback-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .feedback-actions {
          display: flex;
          gap: 15px;
          margin-top: 15px;
          justify-content: center;
        }

        .revise-btn, .new-valuation-btn {
          padding: 12px 20px;
          border: 2px solid #007bff;
          background: white;
          color: #007bff;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 600;
          transition: all 0.2s;
        }

        .revise-btn:hover, .new-valuation-btn:hover {
          background: #007bff;
          color: white;
        }

        .revision-list {
          margin-top: 15px;
        }

        .revision-item {
          background: #f8f9fa;
          padding: 15px;
          margin-bottom: 10px;
          border-radius: 8px;
          border-left: 4px solid #007bff;
        }

        .revision-question {
          font-weight: 600;
          margin-bottom: 5px;
          color: #2c3e50;
        }

        .revision-answer {
          color: #7f8c8d;
          margin-bottom: 10px;
          font-style: italic;
        }

        .revise-specific-btn {
          padding: 8px 16px;
          background: #28a745;
          color: white;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-size: 12px;
        }

        .final-actions {
          margin-top: 15px;
          text-align: center;
        }

        .new-valuation-final-btn {
          padding: 15px 30px;
          background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
          color: white;
          border: none;
          border-radius: 25px;
          cursor: pointer;
          font-size: 16px;
          font-weight: 600;
          box-shadow: 0 4px 15px rgba(40, 167, 69, 0.3);
        }

        .revision-shortcut {
          margin-top: 15px;
          padding-top: 15px;
          border-top: 1px solid rgba(255,255,255,0.1);
        }

        .quick-revise-btn {
          padding: 10px 20px;
          background: rgba(255, 193, 7, 0.2);
          border: 2px solid #ffc107;
          color: #ffc107;
          border-radius: 20px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 600;
          transition: all 0.3s;
        }

        .answered-indicator {
          margin-top: 10px;
          padding: 8px 12px;
          background: rgba(40, 167, 69, 0.2);
          color: #28a745;
          border-radius: 6px;
          font-size: 14px;
          font-weight: 600;
        }

        .message.feedback_success .message-content {
          background: #2c3e50;
          color: white;
        }

        .messages {
          flex: 1;
          overflow-y: auto;
          padding: 30px;
          background: transparent;
          scrollbar-width: thin;
          scrollbar-color: rgba(255, 255, 255, 0.2) transparent;
        }
        
        @media (max-width: 768px) {
          .messages {
            padding: 15px;
          }
        }

        .messages::-webkit-scrollbar {
          width: 6px;
        }

        .messages::-webkit-scrollbar-track {
          background: transparent;
        }

        .messages::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.2);
          border-radius: 3px;
        }

        .messages {
          flex: 1;
          overflow-y: auto;
          padding: 20px;
          background: #f8f9fa;
        }

        .welcome-message {
          text-align: center;
          color: #6c757d;
          margin-top: 100px;
        }

        .examples {
          margin-top: 15px;
          padding: 10px;
          background: #e9ecef;
          border-radius: 8px;
        }

        .message {
          margin-bottom: 15px;
          display: flex;
          flex-direction: column;
        }

        .message.user {
          align-items: flex-end;
        }

        .message.assistant {
          align-items: flex-start;
        }

        .message-content {
          max-width: 70%;
          padding: 12px 16px;
          border-radius: 18px;
          word-wrap: break-word;
        }
        
        @media (max-width: 768px) {
          .message-content {
            max-width: 85%;
            padding: 10px 14px;
            font-size: 14px;
          }
        }

        .message.user .message-content {
          background: #007bff;
          color: white;
        }

        .message.assistant .message-content {
          background: white;
          border: 1px solid #e1e8ed;
          color: #2c3e50;
        }

        .message.error .message-content {
          background: #f8d7da;
          color: #721c24;
          border-color: #f5c6cb;
        }

        .detection-card {
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(10px);
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
          border: 1px solid rgba(255, 255, 255, 0.2);
          max-width: 90%;
          margin-bottom: 16px;
        }

        .detection-content {
          display: flex;
          gap: 16px;
          padding: 20px;
        }

        .detection-image {
          flex-shrink: 0;
          width: 80px;
          height: 80px;
          border-radius: 8px;
          overflow: hidden;
          background: #f8f9fa;
        }

        .detection-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .detection-info {
          flex: 1;
          min-width: 0;
        }

        .detection-header {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          margin-bottom: 12px;
        }

        .detection-icon {
          font-size: 24px;
          flex-shrink: 0;
        }

        .detection-text {
          flex: 1;
          min-width: 0;
        }

        .detected-name {
          font-size: 18px;
          font-weight: 600;
          color: #1a1a1a;
          margin: 0 0 4px 0;
          line-height: 1.3;
        }

        .detection-meta {
          display: flex;
          align-items: center;
          gap: 12px;
          font-size: 13px;
        }

        .category {
          background: #e3f2fd;
          color: #1976d2;
          padding: 2px 8px;
          border-radius: 12px;
          font-weight: 500;
          text-transform: capitalize;
        }

        .confidence {
          color: #666;
          font-weight: 500;
        }

        .detection-message {
          font-size: 14px;
          color: #555;
          line-height: 1.4;
          margin: 0;
        }

        .detection-actions {
          display: flex;
          gap: 8px;
          padding: 16px 20px;
          background: #f8f9fa;
          border-top: 1px solid rgba(0, 0, 0, 0.06);
        }

        .action-btn {
          flex: 1;
          padding: 10px 16px;
          border: none;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
        }

        .action-btn.confirm {
          background: #4caf50;
          color: white;
        }

        .action-btn.confirm:hover {
          background: #45a049;
          transform: translateY(-1px);
        }

        .action-btn.reject {
          background: #f5f5f5;
          color: #666;
          border: 1px solid #ddd;
        }

        .action-btn.reject:hover {
          background: #eeeeee;
          transform: translateY(-1px);
        }

        .action-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }

        /* Mobile responsive */
        @media (max-width: 480px) {
          .detection-content {
            flex-direction: column;
            gap: 12px;
          }

          .detection-image {
            width: 100%;
            height: 120px;
          }

          .detection-actions {
            flex-direction: column;
          }
        }
        
        @media (max-width: 768px) {
          .detection-card {
            max-width: 95%;
            padding: 15px;
          }
          
          .item-name {
            font-size: 18px;
          }
          
          .detection-meta {
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
          }
        }

        .detection-result {
          margin: 15px 0;
        }

        .detected-item {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 10px;
        }

        .item-icon {
          font-size: 24px;
        }

        .item-name {
          font-size: 20px;
          font-weight: bold;
        }

        .detection-meta {
          display: flex;
          gap: 15px;
          align-items: center;
        }

        .category-badge {
          background: rgba(255,255,255,0.2);
          padding: 4px 12px;
          border-radius: 12px;
          font-size: 12px;
          text-transform: uppercase;
          font-weight: bold;
        }

        .confidence-score {
          font-size: 14px;
          opacity: 0.9;
        }

        .detection-message {
          margin-top: 15px;
          font-size: 16px;
          background: rgba(255,255,255,0.1);
          padding: 12px;
          border-radius: 8px;
          line-height: 1.4;
        }

        .uploaded-image {
          margin-bottom: 15px;
          text-align: center;
        }

        .uploaded-image img {
          max-width: 250px;
          max-height: 200px;
          border-radius: 12px;
          box-shadow: 0 4px 12px rgba(0,0,0,0.3);
          border: 2px solid rgba(255,255,255,0.2);
        }

        .detection-actions {
          display: flex;
          gap: 10px;
          margin-top: 15px;
          justify-content: center;
        }

        .confirm-btn, .reject-btn {
          padding: 10px 20px;
          border: none;
          border-radius: 25px;
          font-weight: bold;
          cursor: pointer;
          transition: all 0.3s;
          position: relative;
          overflow: hidden;
        }

        .confirm-btn {
          background: linear-gradient(135deg, #56ab2f 0%, #a8e6cf 100%);
          color: white;
          box-shadow: 0 4px 15px rgba(86, 171, 47, 0.3);
        }

        .confirm-btn:hover {
          background: linear-gradient(135deg, #45a049 0%, #388e3c 100%);
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(76, 175, 80, 0.4);
        }

        .reject-btn {
          background: linear-gradient(135deg, #ff416c 0%, #ff4b2b 100%);
          color: white;
          box-shadow: 0 4px 15px rgba(255, 65, 108, 0.3);
        }

        .reject-btn:hover {
          background: linear-gradient(135deg, #d32f2f 0%, #c62828 100%);
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(244, 67, 54, 0.4);
        }

        .confirm-btn:disabled, .reject-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .message-image {
          margin-bottom: 10px;
        }

        .message-image img {
          max-width: 300px;
          max-height: 300px;
          border-radius: 8px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }

        .valuation-card {
          background: linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%);
          color: #333;
          padding: 30px;
          border-radius: 24px;
          max-width: 90%;
          box-shadow: 0 12px 40px rgba(252, 182, 159, 0.4);
          border: 1px solid rgba(255, 255, 255, 0.3);
          backdrop-filter: blur(15px);
        }
        
        @media (max-width: 768px) {
          .valuation-card {
            max-width: 95%;
            padding: 20px;
          }
          
          .price-range {
            font-size: 22px;
          }
          
          .best-estimate {
            font-size: 18px;
          }
          
          .reasoning {
            font-size: 13px;
          }
        }

        .price-range {
          font-size: 28px;
          font-weight: bold;
          margin: 20px 0;
          text-align: center;
        }

        .best-estimate {
          font-size: 20px;
          margin: 15px 0;
          text-align: center;
        }

        .confidence {
          margin: 15px 0;
          text-align: center;
          font-size: 16px;
        }

        .reasoning {
          margin-top: 20px;
          font-size: 14px;
          line-height: 1.5;
          background: rgba(255,255,255,0.1);
          padding: 15px;
          border-radius: 8px;
        }

        .input-area {
          padding: 20px;
          border-top: 1px solid #e1e8ed;
          background: white;
        }

        .upload-btn, .send-btn, .new-session-btn {
          background: #007bff;
          color: white;
          border: none;
          padding: 12px 24px;
          border-radius: 8px;
          cursor: pointer;
          font-size: 16px;
          transition: background 0.2s;
        }

        .upload-btn:hover, .send-btn:hover, .new-session-btn:hover {
          background: #0056b3;
        }

        .upload-btn:disabled, .send-btn:disabled {
          background: #6c757d;
          cursor: not-allowed;
        }

        .answer-section {
          display: flex;
          gap: 10px;
        }

        .answer-input {
          flex: 1;
          padding: 12px;
          border: 1px solid #ced4da;
          border-radius: 8px;
          font-size: 16px;
        }

        .typing-indicator {
          display: flex;
          gap: 4px;
          padding: 12px 16px;
        }

        .typing-indicator span {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: #6c757d;
          animation: typing 1.4s infinite ease-in-out;
        }

        .typing-indicator span:nth-child(2) {
          animation-delay: 0.2s;
        }

        .typing-indicator span:nth-child(3) {
          animation-delay: 0.4s;
        }

        @keyframes typing {
          0%, 80%, 100% {
            transform: scale(0.8);
            opacity: 0.5;
          }
          40% {
            transform: scale(1);
            opacity: 1;
          }
        }

        .session-complete {
          text-align: center;
        }

        .unified-input {
          width: 100%;
        }

        .input-area {
          padding: 10px;
          background: rgba(255, 255, 255, 0.05);
          border-top: 1px solid rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(20px);
        }
        
        @media (max-width: 768px) {
          .input-area {
            padding: 15px 20px;
          }
          
          .input-field {
            flex-direction: column;
            gap: 10px;
            padding: 15px;
          }
          
          .smart-input-field {
            font-size: 16px;
            padding: 12px 0;
          }
          
          .attach-btn, .smart-submit-btn {
            width: 100%;
            padding: 12px;
            font-size: 16px;
          }
          
          .question-options {
            flex-direction: column;
            gap: 10px;
          }
          
          .question-option-btn {
            width: 100%;
            padding: 12px;
            font-size: 16px;
          }
          
          .feedback-buttons {
            flex-direction: column;
            gap: 10px;
          }
          
          .feedback-btn {
            width: 100%;
            padding: 15px;
            font-size: 16px;
          }
          
          .detection-actions {
            flex-direction: column;
            gap: 10px;
          }
          
          .confirm-btn, .reject-btn {
            width: 100%;
            padding: 15px;
            font-size: 16px;
          }
        }

        .input-field {
          display: flex;
          gap: 12px;
          align-items: center;
          background: rgba(255, 255, 255, 0.1);
          border: 1px solid rgba(255, 255, 255, 0.2);
          border-radius: 16px;
          padding: 12px 20px;
          transition: all 0.3s ease;
        }

        .input-field:focus-within {
          border-color: #667eea;
          background: rgba(255, 255, 255, 0.15);
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
        }

        .smart-input-field {
          flex: 1;
          padding: 8px 0;
          border: none;
          outline: none;
          font-size: 16px;
          background: transparent;
          color: white;
          font-weight: 400;
        }

        .smart-input-field::placeholder {
          color: rgba(255, 255, 255, 0.5);
        }

        .attach-btn {
          padding: 10px 12px;
          background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%);
          border: none;
          border-radius: 50%;
          cursor: pointer;
          font-size: 18px;
          transition: all 0.3s ease;
          color: rgba(255, 255, 255, 0.8);
          box-shadow: 0 2px 8px rgba(255, 255, 255, 0.1);
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .attach-btn:hover {
          background: linear-gradient(135deg, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0.1) 100%);
          transform: scale(1.1) rotate(5deg);
          box-shadow: 0 4px 16px rgba(255, 255, 255, 0.2);
        }

        .smart-submit-btn {
          padding: 12px 24px;
          background: #007bff;
          border: none;
          color: white;
          border-radius: 8px;
          cursor: pointer;
          font-size: 16px;
          font-weight: 600;
          transition: all 0.2s ease;
          box-shadow: 0 2px 8px rgba(0, 123, 255, 0.3);
        }

        .smart-submit-btn:hover {
          background: #0056b3;
          box-shadow: 0 4px 12px rgba(0, 123, 255, 0.4);
        }

        .smart-submit-btn:active {
          transform: translateY(0) scale(0.98);
          transition: all 0.1s ease;
        }

        .smart-submit-btn::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
          transition: left 0.5s;
        }

        .smart-submit-btn:hover::before {
          left: 100%;
        }

        .smart-submit-btn:disabled, .attach-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .message.text_input .message-content {
          background: #17a2b8;
          color: white;
        }

        .options-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
          gap: 8px;
          margin-bottom: 15px;
        }

        .custom-input {
          display: flex;
          gap: 10px;
          padding-top: 10px;
          border-top: 1px solid #e1e8ed;
        }

        .custom-answer-input {
          flex: 1;
          padding: 10px;
          border: 1px solid #ced4da;
          border-radius: 6px;
          font-size: 14px;
        }

        .custom-send-btn {
          padding: 10px 16px;
          background: #28a745;
          color: white;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-size: 14px;
        }

        .custom-send-btn:hover {
          background: #218838;
        }

        .chat-input {
          display: flex;
          gap: 10px;
        }

        .chat-answer-input {
          flex: 1;
          padding: 12px;
          border: 1px solid #ced4da;
          border-radius: 8px;
          font-size: 16px;
        }

        .chat-send-btn {
          padding: 12px 20px;
          background: #007bff;
          color: white;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          font-size: 16px;
        }

        .chat-send-btn:hover {
          background: #0056b3;
        }

        .chat-send-btn:disabled {
          background: #6c757d;
          cursor: not-allowed;
        }

        .option-btn {
          padding: 10px 16px;
          border: 2px solid #007bff;
          background: white;
          color: #007bff;
          border-radius: 20px;
          cursor: pointer;
          transition: all 0.2s;
          font-size: 14px;
        }

        .option-btn:hover {
          background: #007bff;
          color: white;
        }

        .option-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .text-input {
          display: flex;
          gap: 10px;
        }

        .question-options {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          margin-top: 10px;
        }

        .question-option-btn {
          padding: 8px 16px;
          border: none;
          background: linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%);
          color: #667eea;
          border-radius: 25px;
          cursor: pointer;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          font-size: 14px;
          position: relative;
          overflow: hidden;
          box-shadow: 0 2px 8px rgba(102, 126, 234, 0.2);
        }

        .question-option-btn:hover {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(102, 126, 234, 0.4);
        }

        .question-option-btn:active {
          transform: scale(0.95);
        }

        .question-option-btn::after {
          content: '';
          position: absolute;
          top: 50%;
          left: 50%;
          width: 0;
          height: 0;
          background: rgba(0, 123, 255, 0.1);
          border-radius: 50%;
          transform: translate(-50%, -50%);
          transition: width 0.3s, height 0.3s;
        }

        .question-option-btn:active::after {
          width: 300px;
          height: 300px;
        }

        .question-option-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        
        .login-required {
          text-align: center;
          padding: 20px;
          background: rgba(255, 255, 255, 0.1);
          border-radius: 12px;
          border: 1px solid rgba(255, 255, 255, 0.2);
        }
        
        .login-required p {
          color: rgba(255, 255, 255, 0.8);
          margin-bottom: 15px;
          font-size: 16px;
        }
        
        .login-required-btn {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          border: none;
          padding: 12px 24px;
          border-radius: 8px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }
        
        .login-required-btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
        }

        .rejection-card {
          background: linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%);
          border-radius: 20px;
          padding: 28px;
          margin: 20px 0;
          box-shadow: 0 10px 40px rgba(255, 154, 158, 0.4);
          border: 1px solid rgba(255, 255, 255, 0.3);
          backdrop-filter: blur(12px);
        }

        .rejection-content {
          text-align: center;
          color: white;
        }

        .rejection-icon {
          font-size: 48px;
          margin-bottom: 16px;
        }

        .rejection-text {
          font-size: 16px;
          line-height: 1.6;
          margin-bottom: 24px;
          white-space: pre-line;
        }

        .restart-actions {
          display: flex;
          justify-content: center;
        }

        .restart-btn {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border: none;
          color: white;
          border-radius: 25px;
          padding: 12px 24px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
          transition: all 0.3s ease;
          box-shadow: 0 4px 16px rgba(102, 126, 234, 0.3);
        }

        .restart-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(76, 175, 80, 0.4);
        }

        .restart-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }
        
        .admin-btn, .login-btn, .logout-btn {
          background: rgba(255, 255, 255, 0.1);
          color: white;
          border: 1px solid rgba(255, 255, 255, 0.2);
          padding: 10px;
          border-radius: 50%;
          font-size: 18px;
          cursor: pointer;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 40px;
          height: 40px;
          backdrop-filter: blur(10px);
        }
        
        .admin-btn {
          background: linear-gradient(135deg, #ff6b35 0%, #f7931e 100%);
          border-color: transparent;
        }
        
        .login-btn {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border-color: transparent;
        }
        
        .logout-btn {
          background: linear-gradient(135deg, #ff416c 0%, #ff4b2b 100%);
          border-color: transparent;
        }
        
        .admin-btn:hover, .login-btn:hover, .logout-btn:hover {
          transform: translateY(-1px) scale(1.05);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
        }
        
        .header-right {
          display: flex;
          align-items: center;
          gap: 15px;
        }
        
        .user-info {
          display: flex;
          align-items: center;
          gap: 12px;
        }
        
        .user-email {
          color: rgba(255, 255, 255, 0.9);
          font-size: 14px;
          background: rgba(255, 255, 255, 0.1);
          padding: 6px 12px;
          border-radius: 20px;
        }
      `}</style>
    </div>
  );
};

export default AssetValuator;